import React, { useState } from 'react';

import { countryCodeFor } from '../../constants/CountryCodeConstants';

interface CountryFlagProps {
  /** The country name as the course rows spell it. Null means "every country": the globe. */
  name: string | null;
  className?: string;
}

// The world, for "All countries" and for any country we have no flag for.
//
// An emoji rather than an image: it is the one picture every phone and desktop already draws in
// colour, so it sits next to the flags without looking like a different kind of thing. Flag emoji
// would have done the same for the countries, but Windows draws those as two letters ("ZA"), which
// is why the flags themselves are SVG files and only the globe is an emoji.
export const WorldGlobe: React.FC<{ className?: string }> = ({
  className = '',
}) => (
  // Decorative, like the flags: the words next to it already say which place it is.
  <span aria-hidden className={`text-2xl leading-none ${className}`}>
    🌍
  </span>
);

// A country's flag, from the flag-icons set in `public/flags/` (MIT, licence in that folder).
//
// The 4x3 set, not the 1x1: the square crops cut into some flags, and a flag is only worth showing
// if it is recognised at a glance. Served as static files, so each one loads once and the browser
// caches it; a list of 180 countries only fetches the rows that are actually scrolled into view.
const CountryFlag: React.FC<CountryFlagProps> = ({ name, className = '' }) => {
  // Two countries in the table no longer exist (Serbia and Montenegro, Netherlands Antilles) and
  // flag-icons has no file for them, so a failed load falls back to the globe rather than a broken
  // image icon.
  const [failed, setFailed] = useState(false);
  const code = countryCodeFor(name);

  if (code == null || failed) {
    return <WorldGlobe className={className} />;
  }

  return (
    <img
      src={`/flags/4x3/${code.toLowerCase()}.svg`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`h-6 w-8 rounded-[3px] object-cover ring-1 ring-black/30 ${className}`}
    />
  );
};

export default CountryFlag;
